import {
    ActivityProduct,
    ActivityTicket,
    ActivityTicketPax,
    AddonProduct,
    Agency,
    AppliedDiscount,
    Bonus,
    BookingSalesChannel,
    CabinClass,
    CancellationPolicy,
    Cart,
    CartAffiliate,
    FarePassengerType,
    FerryAccommodationType,
    FerryLeg,
    FerryPax,
    FerryProduct,
    FerrySegment,
    FlightLeg,
    FlightPax,
    FlightPriceBreakdown,
    FlightPriceBreakdownByFareType,
    FlightProduct,
    FlightSegment,
    GordianSeating,
    HotelAddress,
    GeoLocation,
    HotelProduct,
    IncludedBaggage,
    MandatoryField,
    PaxDescription,
    PaymentMethodType,
    SerializationType,
    ProductType,
    Room,
    SeatAvailability,
    ServicePackageAddon,
    FlightBrandedFare,
    FlightBrandedFareAttribute,
} from '@/api/Cart/cart.interfaces';
import { generateUUID } from '@shared/utils/uuid';
import {
    getDateAddingDuration,
    getDateSubtractingDuration,
    getFormattedDate,
    getShortDate,
} from '@shared/helpers/datetime/datetime';
import {
    AdditionalBaggage,
    Baggage,
    BaggagePriceBreakdown,
    BaggageType,
} from '@shared/interfaces/baggage';
import { FerryVehicle, FerryVehicleType } from '@shared/interfaces/ferry';
import {
    getStoredFakeData,
    setStoredFakeData,
} from '@/components/shared/DevPanel/DevPanel.helpers';
import { groupBy, random, range, sample, sampleSize, sum } from 'lodash';
import { PassengerType } from '../Checkout/checkout.interfaces';
import { Money } from '@libs/money/money.interfaces';
import { getTravelTime } from '@ui/components/Journey/Journey.helpers';
import { getProductFromDate, getProductToDate } from './cart.helpers';
import { AnyProduct } from '@shared/interfaces/product';
import {
    FakeDataProduct,
    DevCancellationType,
    FakeDataProductFlight,
    FakeDataProductFerry,
} from '@/components/shared/DevPanel/DevPanel.interfaces';
import { getFakeBookingToken } from '@/helpers/queryStrings';
import { Market } from '@shared/config/market/market.models';
import { getCurrentMarketSpecs } from '@shared/config/market/market.helpers';
import { DateFormat } from '@shared/interfaces/dates';
import {
    FlightBrandedAttributeStatus,
    FlightBrandedAttributeType,
} from '@shared/interfaces/brandedFares';

const market: Market = getCurrentMarketSpecs();
const Currency = market.moneyFormat.currency;

interface Trip {
    products: AnyProduct[];
    dates: Dates;
    isRoundTrip: boolean;
}

interface Dates {
    from: Date;
    to?: Date | null;
}

interface AvailableHotel {
    Description: string;
    Destination: string;
    Address: HotelAddress | null;
    PolygonId: number;
    Position: GeoLocation | null;
    ImageUrl: string;
}

let stored = getStoredFakeData();
const search = stored.checkout.search.enabled
    ? stored.checkout.search
    : {
          adults: {
              min: 1,
              max: 1,
          },
          children: {
              min: 1,
              max: 1,
          },
          infants: {
              min: 1,
              max: 1,
          },
      };

const AvailableFerryVehicles = [
    {
        Type: FerryVehicleType.Vehicle6mLong,
        Description: 'Car, max 2 m high, 6 m long',
        LicensePlate: null,
    },
    {
        Type: FerryVehicleType.MotorBike,
        Description: 'Motorbike',
        LicensePlate: null,
    },
    {
        Type: FerryVehicleType.Bicycle,
        Description: 'Bicycle',
        LicensePlate: null,
    },
    {
        Type: FerryVehicleType.MiniBus4mHigh6mLong,
        Description: 'Minibus, 4 m high, 6 m long',
        LicensePlate: null,
    },
    {
        Type: FerryVehicleType.Vehicle4m70cmLong,
        Description: 'Car, 4.7 m long',
        LicensePlate: null,
    },
    {
        Type: FerryVehicleType.MultiPurposeVehicle,
        Description: 'MultiPurpose vehicle',
        LicensePlate: null,
    },
    {
        Type: FerryVehicleType.MiniBus4mHigh6mLong,
        Description: 'Minibus, 4 m high, 6 m long',
        LicensePlate: null,
    },
];

const AvailableHotels: AvailableHotel[] = [
    {
        Description: 'Queens Park Hotel',
        Destination: 'London, England, Storbritannien',
        PolygonId: 541038,
        Position: {
            Latitude: 51.52320379321331,
            Longitude: -0.12773752212524,
        },
        Address: {
            StreetAddress: null,
            City: 'London',
            ZipCode: null,
            Country: 'England',
        },
        ImageUrl:
            'https://images.sembo.travel/ImageService/ImageHandler.ashx?service=url&nameOfImage=https%3A%2F%2Fi.travelapi.com%2Flodging%2F1000000%2F20000%2F14800%2F14754%2F5d491b5b_z.jpg&width=800&height=800&semboApp=true',
    },
    {
        Description: `Scandic Oceanhamnen`,
        Destination: 'Helsingborg, Sverige',
        PolygonId: 527766,
        Position: {
            Latitude: 56.04233,
            Longitude: 12.69002,
        },
        Address: {
            StreetAddress: null,
            City: 'Helsingborg',
            ZipCode: null,
            Country: 'Sverige',
        },
        ImageUrl:
            'https://images.sembo.travel/ImageService/ImageHandler.ashx?service=sembo&nameOfImage=208120.jpg&width=800&height=800&semboApp=true',
    },
    {
        Description: `Splendid Hotel & Spa Nice`,
        Destination: 'Nice, Frankrike',
        PolygonId: 362354,
        Position: {
            Latitude: 43.698555594104896,
            Longitude: 7.259862720966339,
        },
        Address: {
            StreetAddress: '50, Boulevard Victor Hugo',
            City: 'NICE',
            ZipCode: 'F-06000',
            Country: 'France',
        },
        ImageUrl:
            'https://images.sembo.travel/ImageService/ImageHandler.ashx?service=sembo&nameOfImage=181226.jpg&resizeMode=FitOutside&width=320&height=320&formatSettings=jpeg(quality-90)',
    },
    {
        Description: 'BG Hotel Tonga Tower',
        Destination: 'Santa Margalida, Spanien',
        PolygonId: 524904,
        Position: {
            Latitude: 39.76451,
            Longitude: 3.14938,
        },
        Address: {
            StreetAddress: 'Carretera Alcudia-Arta S/N ',
            City: 'Santa Margalida  Mallorca',
            ZipCode: '7458',
            Country: 'ES',
        },
        ImageUrl:
            'https://images.sembo.travel/ImageService/ImageHandler.ashx?service=url&nameOfImage=https:%2f%2fi.travelapi.com%2flodging%2f2000000%2f1130000%2f1120700%2f1120654%2f2a7bc7a9_z.jpg&resizeMode=FitOutside&width=320&height=320&formatSettings=jpeg(quality-90)',
    },
];

export const AvailableCompanies = [
    { code: 'FR', name: 'Ryanair' },
    { code: 'BT', name: 'Air Baltic' },
    { code: 'AC', name: 'Air Canada' },
    { code: 'AF', name: 'Air France' },
    { code: 'AA', name: 'American Airlines' },
    { code: 'RC', name: 'Atlantic Airways' },
    { code: 'OS', name: 'Austrian Airlines' },
    { code: 'BA', name: 'British Airways' },
    { code: 'SN', name: 'Brussels Airlines' },
    { code: 'CX', name: 'Cathay Pacific' },
    { code: 'EK', name: 'Emirates' },
    { code: 'ET', name: 'Ethiopian airlines' },
    { code: 'AY', name: 'Finnair' },
    { code: 'IB', name: 'Iberia' },
    { code: 'KL', name: 'KLM' },
    { code: 'LO', name: 'LOT' },
    { code: 'LH', name: 'Lufthansa' },
    { code: 'DY', name: 'Norwegian Air Shuttle' },
    { code: 'QF', name: 'Qantas' },
    { code: 'QR', name: 'Qatar Airways' },
    { code: 'SK', name: 'SAS' },
    { code: 'LX', name: 'Swiss' },
    { code: 'TG', name: 'Thai Airways' },
    { code: 'TK', name: 'Turkish Airlines' },
];

const getAvailableCompanyByCode = (code?: string) => {
    if (!code) return;

    return AvailableCompanies.find((x) => x.code === code);
};

// const combinationId = 'fake-combination-id-1234567';

// const getCombinationId = () => {
//     const type = stored.checkout.combinationIdType;
//     switch (type) {
//         case CombinationIdType.All:
//             return combinationId;
//         case CombinationIdType.Some:
//             return sample([1, 2]) === 1 ? combinationId : null;
//         default:
//             return null;
//     }
// };

const generateCancellationPolicies = (
    itemIndex: number,
    totalAmount: number,
    totalRooms: number,
    productId: string
): CancellationPolicy[] => {
    const policies: CancellationPolicy[] = [];
    const now = new Date();
    const cancellationType = stored.checkout.cancellationType;

    let hasFreeCancellation = false;

    if (cancellationType === DevCancellationType.Full) {
        hasFreeCancellation = true;
    } else if (cancellationType === DevCancellationType.Random) {
        const isFree = sample([0, 1]) === 1;
        hasFreeCancellation = isFree;

        if (itemIndex === 0 && hasFreeCancellation) {
            const allFree = sample([0, 1]) === 0;

            if (allFree) {
                hasFreeCancellation = true;
            }
        } else if (itemIndex > 0 && hasFreeCancellation) {
            const firstPolicies = generateCancellationPolicies(
                0,
                totalAmount,
                totalRooms,
                productId
            );
            const firstRoomFree = firstPolicies.some((policy) => policy.Amount.Amount === 0);
            hasFreeCancellation = firstRoomFree;
        }
    }

    const includeInitialFree = sample([0, 1]) === 1;

    if (hasFreeCancellation && includeInitialFree) {
        policies.push({
            CancellationTime: getShortDate(now),
            Amount: {
                Amount: 0,
                Currency,
            },
            FullCost: false,
        });
    } else if (!hasFreeCancellation) {
        policies.push({
            CancellationTime: getShortDate(now),
            Amount: {
                Amount: totalAmount * 0.25,
                Currency,
            },
            FullCost: false,
        });
    }

    const daysOffset = hasFreeCancellation ? 7 : (itemIndex + 1) * 5;

    policies.push({
        CancellationTime: getShortDate(
            getDateAddingDuration(now, {
                days: daysOffset,
            })
        ),
        Amount: {
            Amount: totalAmount,
            Currency,
        },
        FullCost: true,
    });

    return policies;
};

const generateActivityTicketPax = (): ActivityTicketPax => {
    return {
        PersonalIdNumber: null,
        Email: null,
        Phone: null,
        Nationality: null,
        Title: null,
        Gender: null,
        Organization: null,
        PassportNumber: null,
        PassportExpirationDate: null,
        Address: null,
        Language: null,
        TicketFields: [
            {
                Id: 'bool',
                Question: 'Do you need equipment?',
                Answer: [],
                ExpectedAnswerType: 4,
                AnswerOptions: [],
                CanSelectMultipleOptions: false,
                Placeholder: '',
            },
            {
                Id: 'datetime',
                Question: 'When do you arrive?',
                Answer: [],
                ExpectedAnswerType: 6,
                AnswerOptions: [],
                CanSelectMultipleOptions: false,
                Placeholder: 'Date',
            },
            {
                Id: 'decimal',
                Question: 'How much do you weigh?',
                Placeholder: 'Weight',
                Answer: [],
                ExpectedAnswerType: 3,
                AnswerOptions: [],
                CanSelectMultipleOptions: false,
            },
            {
                Id: 'options_single_choice',
                Question: 'What is your favourite color?',
                Answer: [],
                ExpectedAnswerType: 7,
                AnswerOptions: [
                    {
                        Id: 'green',
                        Value: 'Green',
                    },
                    {
                        Id: 'yellow',
                        Value: 'Yellow',
                    },
                    {
                        Id: 'blue',
                        Value: 'Blue',
                    },
                    {
                        Id: 'red',
                        Value: 'Red',
                    },
                ],
                CanSelectMultipleOptions: false,
                Placeholder: 'Select your favourite color',
            },
            {
                Id: 'options_multi_choice',
                Question: 'Where have you been?',
                Answer: [],
                ExpectedAnswerType: 7,
                AnswerOptions: [
                    {
                        Id: 'europe',
                        Value: 'Europe',
                    },
                    {
                        Id: 'north_america',
                        Value: 'North America',
                    },
                    {
                        Id: 'south_america',
                        Value: 'South America',
                    },
                    {
                        Id: 'africa',
                        Value: 'Africa',
                    },
                ],
                CanSelectMultipleOptions: true,
                Placeholder: 'Choose where have you been',
            },
            {
                Id: 'pick_up_options',
                Question: 'Choose your pick up point',
                Answer: [],
                ExpectedAnswerType: 8,
                AnswerOptions: [
                    {
                        Id: 'CUSTOM_PICKUP',
                        Value: 'Write your pickup point',
                    },
                    {
                        Id: 'hotel_barcelo',
                        Value: 'Hotel Barceló',
                    },
                    {
                        Id: 'hotel_iberostar',
                        Value: 'Hotel Iberostar',
                    },
                    {
                        Id: 'hotel_roc',
                        Value: 'Hotel Roc',
                    },
                ],
                CanSelectMultipleOptions: false,
                Placeholder: 'Write your pick up point',
            },
            {
                Id: 'how_is_the_weather',
                Question: 'How is the weather?',
                Answer: [],
                ExpectedAnswerType: 0,
                AnswerOptions: [],
                CanSelectMultipleOptions: false,
                Placeholder: 'Type the weather',
            },
            {
                Id: 'long_text',
                Question: 'Long Text',
                Answer: ['Long text default value'],
                ExpectedAnswerType: 1,
                AnswerOptions: [],
                CanSelectMultipleOptions: false,
                Placeholder: 'Long text placeholder',
            },
            {
                Id: 'int',
                Question: 'int',
                Answer: [],
                ExpectedAnswerType: 2,
                AnswerOptions: [],
                CanSelectMultipleOptions: false,
                Placeholder: 'int',
            },
            {
                Id: 'date',
                Question: 'date',
                Answer: [],
                ExpectedAnswerType: 5,
                AnswerOptions: [],
                CanSelectMultipleOptions: false,
                Placeholder: 'Date',
            },
        ],
        FirstName: null,
        LastName: null,
        SpecialNeedsOption: null,
        DateOfBirth: null,
        MandatoryFields: [
            'PERSONAL_ID_NUMBER',
            'PHONE_NUMBER',
            'TITLE', // TODO: Activities team to check if this could be still mandatory
            'FIRST_NAME',
            'LAST_NAME',
            'EMAIL',
            'NATIONALITY',
            'GENDER',
            'ORGANIZATION',
            'PASSPORT_ID',
            'PASSPORT_EXPIRY',
            'ADDRESS',
            'DATE_OF_BIRTH',
            'LANGUAGE',
        ],
    };
};

const generateActivityTicketPaxSimple = (): ActivityTicketPax => {
    return {
        PersonalIdNumber: null,
        Email: null,
        Phone: null,
        Nationality: null,
        Title: null,
        Gender: null,
        Organization: null,
        PassportNumber: null,
        PassportExpirationDate: null,
        Address: null,
        Language: null,
        TicketFields: [],
        FirstName: null,
        LastName: null,
        SpecialNeedsOption: null,
        DateOfBirth: null,
        MandatoryFields: ['TITLE', 'FIRST_NAME', 'LAST_NAME'],
    };
};

const generateActivityTicket = (
    count: number,
    amount: number,
    description: string
): ActivityTicket[] => {
    const ages = ['Young Adult (18-27 years)', 'Adult (+18)', 'Child (4-12)', 'Any age'];

    return [...Array(count)].map((_, index): ActivityTicket => {
        const id = generateUUID();
        return {
            Id: id,
            Description: `${description}, ${sample(ages)}`,
            Pax:
                index % 2 === 0
                    ? generateActivityTicketPax()
                    : generateActivityTicketPaxSimple(),
            Price: { Amount: amount, Currency },
            CustomerCancellationPolicies: generateCancellationPolicies(
                index,
                amount,
                count,
                id
            ),
            CategoryDescription: `Join us for an engaging and interactive experience designed to bring people together while fostering creativity, learning, and fun. This carefully planned activity combines hands-on participation with collaborative elements, ensuring that participants of all skill levels can contribute meaningfully and enjoy themselves.`,
        };
    });
};

const generateActivity = (index: number, dates: Dates): ActivityProduct => {
    const tickets = generateTravellers().adults;

    const initialAmount = 300;
    const amount = initialAmount * tickets;

    const item = sample([
        {
            description: 'LEGOLAND',
            polygonId: 357239,
            image: 'https://cdn.sanity.io/images/inntox6v/production/fac8699d52408d5cf9ebbaf135a36d78ec709409-1920x1080.jpg?fp-x=0.6&fp-y=0.3&w=1535&h=1152&fit=crop&auto=format',
            location: {
                Latitude: 55.73575,
                Longitude: 9.12636,
            },
        },
        {
            description: 'Inträdesbiljetter till Disneyland® Paris Parks',
            polygonId: 376714,
            image: 'https://cdn.sanity.io/images/inntox6v/production/b6c5934b95de2fab20d97366582dce7b9104d757-500x400.jpg?fp-x=0.5&fp-y=0.5&w=1535&h=864&fit=crop&auto=format',
            location: {
                Latitude: 48.8674,
                Longitude: 2.7833,
            },
        },
        {
            description:
                'ABBA Voyage Express Coach med biljettalternativ från centrala London',
            polygonId: 604093,
            image: 'https://cdn.sanity.io/images/inntox6v/production/e96b57ec72974c5aa12291e1612647ab07dc37e3-720x480.jpg?fp-x=0&fp-y=0.5&w=1535&h=600&fit=crop&auto=format',
            location: {
                Latitude: 51.533,
                Longitude: 0.0112,
            },
        },
    ]);

    return {
        Id: generateUUID(),
        PolygonId: item.polygonId,
        Location: item.location,
        Address: null,
        // CombinationId: getCombinationId(),
        $type: SerializationType.ActivityProduct,
        Type: ProductType.Activity,
        Description: item.description,
        Price: { Amount: amount, Currency },
        AppliedDiscount: null,
        PlaceCode: '3256047',
        IsRefundable: index % 2 === 0,
        ImageUrl: item.image,
        Rating: Math.round(random(2, 10)) / 2,
        Date: getFormattedDate(dates.from),
        Tickets: generateActivityTicket(tickets, initialAmount, item.description),
        Tags: [],
    };
};

const generateFlightBaggagePriceBreakdown = (amount: number): BaggagePriceBreakdown => {
    return {
        BaggagePrice: { Amount: amount * 0.8, Currency },
        BookingFee: { Amount: amount * 0.2, Currency },
        TotalPrice: { Amount: amount, Currency },
    };
};

enum JourneyType {
    OneWay,
    RoundTrip,
    RoundTripSplit,
}

const generateFlightIncludedbaggage = (index: number): IncludedBaggage => {
    if (index % 2 === 0) {
        return {
            Cabin: null,
            CheckedIn: {
                Pieces: 1,
                WeightPerPiece: 0,
                TotalWeight: 0,
                LimitedInfo: false,
                AtLeastOneBagIncluded: false,
            },
        };
    } else if (index % 3 === 0) {
        return {
            Cabin: null,
            CheckedIn: null,
        };
    } else {
        return {
            Cabin: {
                Pieces: 1,
                WeightPerPiece: 0,
                TotalWeight: 23,
                LimitedInfo: false,
                AtLeastOneBagIncluded: false,
            },
            CheckedIn: null,
        };
    }
};

const generateFlightAdditionalBaggage = (
    journey: JourneyType,
    passengerType: PassengerType
): AdditionalBaggage => {
    const splitSector = journey === JourneyType.RoundTripSplit;

    const outboundCount = passengerType === PassengerType.Adult ? 3 : 2;
    const outbound = [...new Array(outboundCount)].map((_, index) =>
        generateBaggage(index, 1, journey)
    );

    const inboundCount = passengerType === PassengerType.Adult ? 1 : 3;
    const inbound = splitSector
        ? [...new Array(inboundCount)].map((_, index) => generateBaggage(index, 2))
        : [];

    return {
        ShowWarning: true,
        SplitSector: splitSector,
        Alternatives: [...outbound, ...inbound],
    };
};

const generateBaggage = (
    index: number,
    sectorNumber: number,
    journey?: JourneyType
): Baggage => {
    const amount = 795;
    const pieces = index + 1;
    const price = pieces * amount;

    const perSector =
        journey === JourneyType.RoundTrip
            ? [{ SectorNumber: 1 }, { SectorNumber: 2 }]
            : [{ SectorNumber: sectorNumber }];

    return {
        Id: generateUUID(),
        Description: '',
        Index: index,
        Pieces: pieces,
        IsSelected: false,
        PerSector: perSector,
        Weight: 23,
        Type: BaggageType.CheckedBag,
        ShowDiscardPopUp: false,
        Price: { Amount: price, Currency },
        PriceBreakdown: generateFlightBaggagePriceBreakdown(amount),
    };
};

const generateFlightPaxDescription = (type: PassengerType): PaxDescription => {
    switch (type) {
        case PassengerType.Child:
            return {
                IsAdult: false,
                Age: 10,
            };
        case PassengerType.Infant:
            return {
                IsAdult: false,
                Age: 1,
            };
        default:
            return {
                IsAdult: true,
                Age: 99,
            };
    }
};

const generateFlightPriceBreakdown = (
    amounts: {
        adult: number;
        child: number;
        infant: number;
    },
    pax: {
        adults: number;
        children: number;
        infants: number;
    }
): FlightPriceBreakdown => {
    const createFareType = (
        fareType: FarePassengerType,
        numberOfPax: number,
        amount: number
    ): FlightPriceBreakdownByFareType => {
        const currency = 'SEK';
        const price = amount * 0.5;
        const taxAndFee = amount * 0.42;
        const fee = amount * 0.008;

        return {
            FareType: fareType,
            NumberOfPax: numberOfPax,
            FlightPrice: { Amount: price, Currency: currency },
            TaxesAndFees: { Amount: taxAndFee, Currency: currency },
            BookingFee: { Amount: fee, Currency: currency },
            TotalPrice: { Amount: amount, Currency: currency },
        };
    };

    const fareTypes = [
        pax.adults && createFareType(FarePassengerType.Adult, pax.adults, amounts.adult),
        pax.children && createFareType(FarePassengerType.Child, pax.children, amounts.child),
        pax.infants && createFareType(FarePassengerType.Infant, pax.infants, amounts.infant),
    ].filter(Boolean) as FlightPriceBreakdownByFareType[];

    return {
        FareTypes: fareTypes,
        RebookableFee: {
            Amount: amounts.adult + 179,
            Currency,
        },
    };
};

const generateFlightPax = (
    count: number,
    type: PassengerType,
    isOneWay: boolean,
    index: number
): FlightPax[] => {
    return [...Array(count)].map((): FlightPax => {
        const mandatoryFields = [
            MandatoryField.Gender,
            MandatoryField.FirstName,
            MandatoryField.LastName,
        ];

        if (type !== PassengerType.Adult) {
            mandatoryFields.push(MandatoryField.DateOfBirth);
        }

        const isInfant = type === PassengerType.Infant;

        if (!isInfant) {
            mandatoryFields.push(MandatoryField.Baggage);
        }

        const journey = isOneWay
            ? JourneyType.OneWay
            : index % 3 === 0
              ? JourneyType.RoundTripSplit
              : JourneyType.RoundTrip;

        return {
            AdditionalBaggage: !isInfant
                ? generateFlightAdditionalBaggage(journey, type)
                : {
                      ShowWarning: false,
                      SplitSector: false,
                      Alternatives: [],
                  },
            PaxDescription: generateFlightPaxDescription(type),
            Title: 0,
            Gender: 0,
            Nationality: null,
            FirstName: null,
            LastName: null,
            SpecialNeedsOption: null,
            DateOfBirth: null,
            MandatoryFields: mandatoryFields,
        };
    });
};

const getGordianSession = (index: number): GordianSeating | null => {
    const sessions = [
        {
            TripId: 'e4d121a0-810f-4dd6-99b3-37650ca642e6',
            TripAccessToken:
                'ZXlKaGJHY2lPaUpTVXpJMU5pSXNJblI1Y0NJNklrcFhWQ0o5LmV5SjBjbDlwWkNJNkltVTBaREV5TVdFd0xUZ3hNR1l0TkdSa05pMDVPV0l6TFRNM05qVXdZMkUyTkRKbE5pSXNJbWx1WDJsa0lqb2lNelUyWWpoalptSXRNV0l5WVMwME9HTXlMVGt6WXpBdFpHWTJaVEJtWXprME5qZGxJaXdpZG1Gc2FXUmZkVzUwYVd3aU9pSXlNREl6TFRFeExUQTRWREUwT2pVd09qTXdMamMwTXpZek4xb2lmUS5KN3o4SEFmcEhHdEp2TDN0blVQTmltSzdvUGVnaEhLVG9EeGM4VGRraHV3bWcwX0pIQVlqaGp6VV9PNkk0Z19BcVlQN0U1ZmFadW9hSmlzWHhPdnNZa3pIMXhINmdoVzRGSzhKTFVKMUp6T2h0WUFMOHM0OXNONTUwX1dadWJBb2JDWndGa3I2VVh2ZDNlSU9JbWlzWGpHTWdPZWFTcHU4dmRuVXlDNjlCbXI2bUFqazFtWXNmNS1tLWE0YVpQYV8xdVJPUWFnUnRIcHc3NHlHNVU5azlud3RkMVVnNUdFdE80TVJBdEFIN1NZX29FdG12UEdYd0ozMXJ4TVdDYW5zaE5SM0FLTjI1U3dGSzcxd0ZTR2oyb2RNOUx3dVJMdjFwYzZjR09VMzlCSEs5bUppc2l1d1U3UHkyTF9acG95ZVFMdWRPTlUwb0xjZ2FKY0MtMmdVMkE=',
            SearchId: 'b7e6b8be-5f18-4eef-88f5-84cf72c162cc',
            SeatAvailability: SeatAvailability.InProgress,
        },
        {
            TripId: '53ddeb49-37f9-41f8-b78e-f4dcdf935b29',
            TripAccessToken:
                'ZXlKaGJHY2lPaUpTVXpJMU5pSXNJblI1Y0NJNklrcFhWQ0o5LmV5SjBjbDlwWkNJNklqVXpaR1JsWWpRNUxUTTNaamt0TkRGbU9DMWlOemhsTFdZMFpHTmtaamt6TldJeU9TSXNJbWx1WDJsa0lqb2lNelUyWWpoalptSXRNV0l5WVMwME9HTXlMVGt6WXpBdFpHWTJaVEJtWXprME5qZGxJaXdpZG1Gc2FXUmZkVzUwYVd3aU9pSXlNREkwTFRFd0xUQTVWREE1T2pVNU9qQXdMamMwTkRrME5Wb2lmUS51RVY0aXU0SkdJQ1hZOVFlVF9tSm5idE9NR19oOUxOc01CUmRFMGpNVml0aFJwbXNtOF9kM3dsdWd2X2R5ekNEMlpkUnBRWWtDeGpuU3VMU0VEQzBuMU9MQV93SHdEeGttc2FQa1Fwb2Z1a2Y4ZWxrb2ZJczZVM1RXYTh2QmEtRXBaaFJRZE5kdS1iOGwxUTRRWWdVT0cwM2xlS2ZOb21NSjRCZDVJMnUtUlBDaWpDeE56cGRqS1hEamJxVERseHJ6WHBvcUduYlJjTDdlbEZhb2FMMGlVWlI3ek9uaDdpN19NRksxaGZYbjFDbFBRTDVNV3lfQ3ZqR21Pdnh3OUlKSWNZVUpCY0UwNURudGN0QUZoRnRZbzFkcm93ZDVXejIxdE11RUhjdDg2RWR1MVZDUHFTakl1R2xidXpobkx5S3ViVDVNczIxY1ZZWDlMMWdLdFByaXc=',
            SearchId: 'e4c68f47-6fe7-493a-bb89-9eec5a81416a',
            SeatAvailability: SeatAvailability.InProgress,
        },
        {
            TripId: 'e778b6fc-e3a9-4a9c-a761-4731303b6e77',
            TripAccessToken:
                'ZXlKaGJHY2lPaUpTVXpJMU5pSXNJblI1Y0NJNklrcFhWQ0o5LmV5SjBjbDlwWkNJNkltVTNOemhpTm1aakxXVXpZVGt0TkdFNVl5MWhOell4TFRRM016RXpNRE5pTm1VM055SXNJbWx1WDJsa0lqb2lNelUyWWpoalptSXRNV0l5WVMwME9HTXlMVGt6WXpBdFpHWTJaVEJtWXprME5qZGxJaXdpZG1Gc2FXUmZkVzUwYVd3aU9pSXlNREkwTFRFd0xUQTVWREE1T2pVNU9qQXdMamN5TkRrd05Wb2lmUS5CVkdvdTNkWWtmal9BSHRDazI4cndNQldwLWRJSEZ5SWtEOUFKM3AzaU41TkVXMWx1UFlJLTNULWdONnd6aDdFSGE1SkZCRk1NSjg4NzBuc0xmY2VJeFB5VEJxOG94cjNsbjduR0RTQ1RLYkRnVmVTTG9laDE1WmgwR3ZueXYtOWhnUVV5QjM2VE9FS0h5bkVNV3VGalBTdjFobEp5MGdKRWl5VldJNkh6SkxkUUhURUxwNmdEMHZ3MnlpRFNscVVkYkxpSWRVaDdQdkg2YWZseGM5YndEU1AzT3pVbW96SnZWVmVMTmJ6Z2p2V2pETGlCX2tSQWZ1ZUxYcGk1dEg1eHpPQWhOQllTMEdCMlVmU1U0akxHTHRfcGFyRU9jQy1WQkJ5dzlGMUJOV2xXa3c4VlR2VENhWVRCekljWV94REhVQmFOV2VONVREN0JSd3NJZFlZeWc=',
            SearchId: '937560df-4a5b-485a-9c05-724d874c3702',
            SeatAvailability: SeatAvailability.InProgress,
        },
        {
            TripId: 'a1847748-c132-4dd7-9ddd-119ef0d2b400',
            TripAccessToken:
                'ZXlKaGJHY2lPaUpTVXpJMU5pSXNJblI1Y0NJNklrcFhWQ0o5LmV5SjBjbDlwWkNJNkltRXhPRFEzTnpRNExXTXhNekl0TkdSa055MDVaR1JrTFRFeE9XVm1NR1F5WWpRd01DSXNJbWx1WDJsa0lqb2lNelUyWWpoalptSXRNV0l5WVMwME9HTXlMVGt6WXpBdFpHWTJaVEJtWXprME5qZGxJaXdpZG1Gc2FXUmZkVzUwYVd3aU9pSXlNREkwTFRFd0xUQTVWREV3T2pBd09qUTRMamc1T0RJMk0xb2lmUS5WbFFoaGlENzRsZVpFYThadUZDcVJrQ29WTVRFTVVta1BMM1VMUkV1TXAtbEQ3R1htenFuRC1JbG9uOHpRMFJoZkc3WVF4aEFaX1dRM3lQdWRlalBMNHFhOWk5UE5TdTVTdmVaeGN5NTZKWVRNQzMxV2dVQ1gxOTdtTjVQVEFTQmI0MjRfcEtDdDJpUlQxYVE3YXgwRWpFeGhyTTdYN1ZIZlo0cTFzdGdfYm5xcjlTMFNYSUlQcVNHZXl4aF9Ka3Z3REY1MjBvMFVWdWxIVnZ1WFZ0SDBINWQ1UjlhaUFEelNINVF4M0tjQzlGSElIdUpxX1B6ak9MSXZYNExwNEE4ZHppMnpMSTVwam5nVXlVSl9jRHcxNGxrazRvSTBnbzl1MThtSTVDRWtQWEprV25vQVBjZVU2UVloUnVoZFI4bnYzQlEtOTRWUVBtemRWWkFldHJCYlE=',
            SearchId: '255e57cc-54cb-4a75-8155-2183cebd0381',
            SeatAvailability: SeatAvailability.InProgress,
        },
        {
            TripId: '3e820dd3-9acd-4a16-9e0a-617715539cfc',
            TripAccessToken:
                'ZXlKaGJHY2lPaUpTVXpJMU5pSXNJblI1Y0NJNklrcFhWQ0o5LmV5SjBjbDlwWkNJNklqTmxPREl3WkdRekxUbGhZMlF0TkdFeE5pMDVaVEJoTFRZeE56Y3hOVFV6T1dObVl5SXNJbWx1WDJsa0lqb2lNelUyWWpoalptSXRNV0l5WVMwME9HTXlMVGt6WXpBdFpHWTJaVEJtWXprME5qZGxJaXdpZG1Gc2FXUmZkVzUwYVd3aU9pSXlNREkwTFRFd0xUQTVWREV3T2pBNU9qUTVMalE0T0RBMU9Gb2lmUS5LazVRb0IweFQ2aGg4ZTUxb2pMcXlaQTVlTHJsYW9sNjdqMGxtdm9jQ3V0cVhJTnNrSGVnYndIcWl0bl90cmRwQ2FzQ2J3bXFMSGFkTm5CU09kNHV5VGlxOTBnZ1BqMlExS3BfeUtldVNPS3F3bzJ2VjVDajlYLThVYUkxWlN1MHNabVBnb2YxS1lkUUNnYXhDWmlMNnNyNGk1Qko2MzN5eWxrSklVQ2tkYWdzNUlzYk81dUllQVZHMW56VDdBM2sybHQxQzB6M3Z2a3MxTVpYQnB3a0FQNTVEUHpfREo1RnkwYVBfblZ5RWM3Q0tXcElHTlM5VldiQ3BqN28zMmtoNDhxNWpOUG80X0pjV0pMX21ZMEFoZXdxdWdsNzdUcEVBbXVSd3kwWms1XzVTdGE3V1JQR3RQTnBTUmRvdHBDd3hqZEpfU0pkenhBYm5TNHVmYXNldGc=',
            SearchId: '63ab4a51-62c8-4988-8245-9d64736c6bb9',
            SeatAvailability: SeatAvailability.InProgress,
        },
    ];
    return index < sessions.length ? sessions[index] : null;
};

const generateTravellers = () => {
    return {
        adults: sample(range(search.adults.min, search.adults.max + 1)) ?? 1,
        children: sample(range(search.children.min, search.children.max + 1)) ?? 0,
        infants: sample(range(search.infants.min, search.infants.max + 1)) ?? 0,
    };
};

export const generateFlight = (
    index: number,
    outboundFromDate: Date,
    inboundFromDate: Date | null,
    metadata?: {
        isRoundTrip?: boolean;
        carrierCode?: string;
    }
): FlightProduct => {
    const getCarrierLogo = (carrier?: string): string | null => {
        return carrier
            ? `https://cdn.sanity.io/files/inntox6v/production/711e8c33df807606f4f7ec60221d67c6af963821.svg/${carrier}.svg`
            : null;
    };

    const { adults, children, infants } = generateTravellers();

    const indexAmount = index * 100;
    const adultAmount = 600 * adults + indexAmount;
    const childAmount = 300 * children + indexAmount;
    const infantAmount = 200 * infants + indexAmount;

    const amount = sum([adultAmount, childAmount, infantAmount]);

    const isRebookable = sample([1, 2]) === 1;

    const locations = [
        {
            code: 'ARN',
            name: 'Arlanda',
            country: 'Sverige',
            location: 'Stockholm',
        },
        {
            code: 'CPH',
            name: 'Kastrup',
            country: 'Danmark',
            location: 'Köpenhamn',
        },
        {
            code: 'LGW',
            name: 'Gatwick',
            country: 'Storbritannien',
            location: 'London',
        },
        {
            code: 'CDG',
            name: 'Charles de Gaulle',
            country: 'Frankrike',
            location: 'Paris',
        },
        {
            code: 'FCO',
            name: 'Rome-Fiumicinos',
            country: 'Italien',
            location: 'Rome',
        },
        {
            code: 'PMI',
            name: 'Son Sant Joan',
            country: 'Spanien',
            location: 'Palma de Mallorca',
        },
        {
            code: 'BCN',
            name: 'El Prat',
            country: 'Spanien',
            location: 'Barcelona',
        },
    ];

    const getLeg = (inbound = false): FlightLeg => {
        const initialDate = inbound && inboundFromDate ? inboundFromDate : outboundFromDate;
        const segments = getSegments(initialDate);

        const company =
            getAvailableCompanyByCode(metadata?.carrierCode) ?? sample(AvailableCompanies);

        return {
            Segments: segments,
            PlatingCarrier: company?.code ?? '',
            PlatingCarrierName: company?.name ?? '',
            PlatingCarrierLogo: getCarrierLogo(company?.code),
            TravelTime: '3h 25m',
            IncludedBaggage: generateFlightIncludedbaggage(index),
        };
    };

    const getSegments = (date: Date): FlightSegment[] => {
        let previousIndex = Math.floor(Math.random() * locations.length);

        const segmentsCount = sample([1, 2, 3, 4]);
        let previousArrivalDate = date; // Start from the provided date

        return [...new Array(segmentsCount)].map((): FlightSegment => {
            const departureDate = getDateAddingDuration(previousArrivalDate, {
                hours: 1,
            });

            const arrivalDate = getDateAddingDuration(departureDate, {
                hours: 2,
                minutes: 15,
            });

            const nextPreviousIndex = previousIndex + 1;
            const nextIndex = nextPreviousIndex < locations.length ? nextPreviousIndex : 0;
            const departure = locations[previousIndex];
            const arrival = locations[nextIndex];

            const company =
                getAvailableCompanyByCode(metadata?.carrierCode) ??
                sample(AvailableCompanies);

            const operatingCompany =
                getAvailableCompanyByCode(metadata?.carrierCode) ??
                sample(AvailableCompanies);

            previousIndex = nextIndex;
            previousArrivalDate = arrivalDate;

            return {
                ArrivalAirportCode: arrival.code,
                ArrivalAirportLabel: arrival.location,
                ArrivalAirportName: arrival.name,
                ArrivalDate: getFormattedDate(arrivalDate, DateFormat.ISO),
                ArrivalLocationName: arrival.location,
                CabinClass: CabinClass.Economy,
                CompanyLogo: null,
                DepartureAirportCode: departure.code,
                DepartureAirportName: departure.name,
                DepartureAirportLabel: departure.location,
                DepartureDate: getFormattedDate(departureDate, DateFormat.ISO),
                DepartureLocationName: departure.location,
                FlightNumber: `${company?.code}${Math.floor(Math.random() * 9999)}`,
                FlightTime: getTravelTime(departureDate, arrivalDate),
                HasTechnicalStop: segmentsCount === 1,
                MarketingCarrier: company?.code ?? '',
                MarketingCarrierLogoUrl: null,
                MarketingCarrierName: company?.name ?? '',
                OperatingCarrier: operatingCompany?.code ?? '',
                OperatingCarrierName: operatingCompany?.name ?? '',
                StopOverTime: '1h',
            };
        });
    };

    const legs = metadata?.isRoundTrip ? [getLeg(), getLeg(true)] : [getLeg()];
    const isOneWay = legs[1] === undefined;

    const getDescription = (legs: FlightLeg[]) => {
        const firstLeg = legs[0];
        const firstSegment = firstLeg.Segments[0];
        const lastSegment = firstLeg.Segments[firstLeg.Segments.length - 1];
        return `${firstSegment.DepartureAirportName} - ${lastSegment.ArrivalAirportName}`;
    };

    return {
        Id: generateUUID(),
        // CombinationId: getCombinationId(),
        $type: SerializationType.FlightProduct,
        Type: ProductType.Flight,
        Description: getDescription(legs),
        Price: { Amount: amount, Currency },
        AppliedDiscount: null,
        PriceBreakdown: generateFlightPriceBreakdown(
            {
                adult: adultAmount,
                child: childAmount,
                infant: infantAmount,
            },
            {
                adults,
                children,
                infants,
            }
        ),
        Tags: [],
        Seating: null,
        IsRebookable: isRebookable,
        Gordian: getGordianSession(index),
        Pax: [
            ...generateFlightPax(adults, PassengerType.Adult, isOneWay, index),
            ...generateFlightPax(children, PassengerType.Child, isOneWay, index),
            ...generateFlightPax(infants, PassengerType.Infant, isOneWay, index),
        ],
        Legs: legs,
        FlightTermsUrl:
            'https://www.sembo.se/d/mvc/Flight/FlightTerms?itineraryCacheId=H4sIAAAAAAAEAF1T23LaMBClL53O9CcYP4eJbQyJmelD0wLBDKT1RSZk-iAjGQTyZSwR02Tyj_2jVisgceMH9uzRarW7Z2l9aLVaf9UHFr6PAP58ejYEXRuDB2UxMQaGZV7azqVt2k7bdAe93sA0jQtDrNRRZAMi76OuBpZ5ikpzHda-6na74KvExnda4kruK6oDmGJ-zCaAJeCboa_T6_z5Li_qXPlpmik_kDgnuCLGy68Lo6wUE1Y4F2VRSSxZkbdv94kKVulxJoyB6oBBbdh1bZMQs9N30qTj9Ht25_qqhztmYpqUkH7q2HANq1BoKCkUGGEuoMAVsGCVgY5W_OTrzBYgOAqGUwWJxgDSRgp6OJKpKsn4mmFC9-KirSp_pBxK17jRxbEJdnqYnR5mVdLIWdS6-b3GCk5ySaucSnDPRUgJFzhbbzRdrhr3y7frwD-oeVa6I5i9IM1IcWqYi_8U4XQt9JY0FGwKKN8tBWzEcSmoPGYsJROvD4GijxvlzotcebrCZwNDnVa364A88iyBJm2nD_hMsgbJzqQE0u73rxVeK5zvOVepEwm54degv72fq8y1cezz1Y7vJtuCkVtvQzIkljl6wrG7n7CaRbfeI8n4brnwviW2W-G4l6OxWy1jh90xj9OoDO-383g2Hi3mcS9E1qgMbT4NTUugBUFoNKxDy59GaB4HO-8OxRscxLyLUfSEcrL1u5s65ptpaPHFLD54OHNL5dfziJfE5tHMuq-X370xReUoGUt9HiI_iEa-WI5H8xkammh7cxcgX0ThTRCZ_CmID9Nk7E9Qzhm2D1UQlzWJ_QW9nWdRaLqT3PwCk6aggtTrmO6FWkDQpqIEhgT2vBeAxapQ_9tXl4labS3IpiWTerAy5W-L9_Ly-R-ASQTOaQQAAA..',
        DiscountedPrice: { Amount: 0, Currency },
        BrandedFare: generateBrandedFare(legs),
    };
};

const generateBrandedFare = (legs: FlightLeg[]): FlightBrandedFare => {
    const attributes: FlightBrandedFareAttribute[] = [];
    const hasCabinBags = legs.some((l) => l.IncludedBaggage.Cabin);
    const hasCheckedBags = legs.some((l) => l.IncludedBaggage.CheckedIn);

    const attributeTypes = sampleSize(
        Object.values(FlightBrandedAttributeType) as FlightBrandedAttributeType[],
        10
    ).filter(
        (a) =>
            a !== FlightBrandedAttributeType.CarryOn &&
            a !== FlightBrandedAttributeType.CheckedBag
    );

    const title = sample(['Light', 'Classic', 'Economy', 'Plus', 'Business']);

    if (hasCabinBags) {
        attributes.push({
            Type: FlightBrandedAttributeType.CarryOn,
            Status: FlightBrandedAttributeStatus.Included,
        });
    }
    if (hasCheckedBags) {
        attributes.push({
            Type: FlightBrandedAttributeType.CheckedBag,
            Status: FlightBrandedAttributeStatus.Included,
        });
    }

    return {
        BrandName: title,
        Attributes: attributes.concat(
            attributeTypes.map((type) => ({
                Type: type,
                Status: sample(
                    Object.values(FlightBrandedAttributeStatus)
                ) as FlightBrandedAttributeStatus,
            }))
        ),
    };
};

const generateFerryPaxDescription = (type: PassengerType): PaxDescription => {
    switch (type) {
        case PassengerType.Child:
            return {
                IsAdult: false,
                Age: 14,
            };
        case PassengerType.Infant:
            return {
                IsAdult: false,
                Age: 3,
            };
        default:
            return {
                IsAdult: true,
                Age: 99,
            };
    }
};

const generateFerryPax = (count: number, type: PassengerType): FerryPax[] => {
    return [...Array(count)].map((): FerryPax => {
        return {
            PaxDescription: generateFerryPaxDescription(type),
            Title: 0,
            Gender: 0,
            Nationality: null,
            FirstName: null,
            LastName: null,
            SpecialNeedsOption: null,
            DateOfBirth: null,
            MandatoryFields: [
                MandatoryField.Gender,
                MandatoryField.FirstName,
                MandatoryField.LastName,
                MandatoryField.DateOfBirth,
                MandatoryField.Nationality,
                MandatoryField.PassportNumber,
                MandatoryField.PassportExpirationDate,
                MandatoryField.SpecialNeeds,
                MandatoryField.LoyaltyMembership,
            ],
            LoyaltyMembership: null,
            PassportNumber: null,
            PassportExpirationDate: null,
        };
    });
};

const generateFerryVehicles = (numberOfVehicles: number): FerryVehicle[] => {
    const selectedVehicles: FerryVehicle[] = [];

    for (let i = 0; i < numberOfVehicles; i++) {
        const randomIndex = Math.floor(Math.random() * AvailableFerryVehicles.length);
        selectedVehicles.push({ ...AvailableFerryVehicles[randomIndex] });
    }

    return selectedVehicles;
};

const generateFerry = (
    index: number,
    outboundFromDate: Date,
    inboundFromDate: Date | null,
    metadata?: {
        isRoundTrip?: boolean;
    }
): FerryProduct => {
    const { adults, children, infants } = generateTravellers();

    const adultAmount = 500 * adults;
    const childAmount = 250 * children;
    const infantAmount = 125 * infants;

    const amount = sum([adultAmount, childAmount, infantAmount]);

    const getLeg = (inbound = false): FerryLeg => {
        const initialDate = inbound && inboundFromDate ? inboundFromDate : outboundFromDate;
        return { Segments: getSegments(initialDate, inbound) };
    };

    const getSegments = (date: Date, inbound: boolean): FerrySegment[] => {
        const accommodations = inbound
            ? [
                  {
                      Title: '5 berth cabin (Outside, Bunk Beds, Pet Friendly)',
                      Quantity: 1,
                      Type: 1,
                      Price: { Amount: 0, Currency },
                  },
                  {
                      Title: '2 berth cabin (Shared, Outside, Female only)',
                      Quantity: 2,
                      Type: 1,
                      Price: { Amount: 0, Currency },
                  },
                  {
                      Title: 'Reserved Seat (Outside)',
                      Quantity: 1,
                      Type: 0,
                      Price: { Amount: 0, Currency },
                  },
              ]
            : [
                  {
                      Title: 'Test Ferry Accommodation',
                      Quantity: 2,
                      Type: FerryAccommodationType.Cabin,

                      Price: {
                          Currency: 'EUR',
                          Amount: 20,
                      },
                  },
              ];

        const meals = inbound
            ? [
                  {
                      Title: 'Test meal',
                      Quantity: 2,
                      Price: {
                          Currency: 'EUR',
                          Amount: 20,
                      },
                  },
                  {
                      Title: 'Prove meal',
                      Quantity: 1,
                      Price: {
                          Currency: 'EUR',
                          Amount: 50,
                      },
                  },
              ]
            : [];

        const ticketType = inbound
            ? {
                  Name: 'FLEXI',
                  Description:
                      'Ombokningsbar fram till 2 timmar före avgång inom våra ordinarie öppettider. Ingen ändringsavgift. Vid avbokning 15 dagar eller fler innan avresa, ingen kostnad. Sker avbokningen mellan 1-14 dagar före avresa, 50% i kostnad. För avbokning avresedagen eller senare, 100% i kostnad.',
              }
            : {
                  Name: 'ECONOMY',
                  Description:
                      'Ändring av linje, datum och avgångstid kan göras fram till avgång mot en ändringsavgift på 500 SEK per väg + evt. mellanskillnad. Övriga ändringar – ingen avgift. Ingen återbetalning vid avbokning.',
              };

        const direction = inbound
            ? {
                  DeparturePortCode: 'DKFDH',
                  DeparturePortName: 'Frederikshavn',
                  DepartureLocationName: null,
                  ArrivalPortCode: 'SEGOT',
                  ArrivalPortName: 'Gothenburg',
                  ArrivalLocationName: null,
              }
            : {
                  DeparturePortCode: 'SEGOT',
                  DeparturePortName: 'Gothenburg',
                  DepartureLocationName: null,
                  ArrivalPortCode: 'DKFDH',
                  ArrivalPortName: 'Frederikshavn',
                  ArrivalLocationName: null,
              };

        return [
            {
                ...direction,
                FerryName: 'Jutlanica',
                Company: 'Stena Line',
                TicketType: ticketType,
                Vehicle: null,
                Vehicles: generateFerryVehicles(sample([1, 2, 3, 4, 5])),
                DepartureDate: getFormattedDate(date, DateFormat.ISO),
                ArrivalDate: getFormattedDate(
                    getDateAddingDuration(date, { hours: 2 }),
                    DateFormat.ISO
                ),
                CompanyLogo:
                    'https://cdn.sanity.io/files/inntox6v/production/9043ce3577c212b0582fa57d652939b2292c6c0e.png/Stena-Line.svg',
                Accommodations: accommodations,
                OriginalAccommodationsIncludedInPrice: [],
                Meals: meals,
                Pets: [],
                Lounges: [],
            },
        ];
    };

    const legs: FerryLeg[] = metadata?.isRoundTrip ? [getLeg(), getLeg(true)] : [getLeg()];

    return {
        Id: generateUUID(),
        // CombinationId: getCombinationId(),
        $type: SerializationType.FerryProduct,
        Type: ProductType.Ferry,
        Description: 'Frederikshavn - Gothenburg',
        Price: { Amount: amount, Currency },
        AppliedDiscount: null,
        Tags: [],
        Pax: [
            ...generateFerryPax(adults, PassengerType.Adult),
            ...generateFerryPax(children, PassengerType.Child),
            ...generateFerryPax(infants, PassengerType.Infant),
        ],
        Legs: legs,
        DiscountedPrice: { Amount: 0, Currency },
    };
};

const generateHotelPaxDescription = (count: number, age: number): PaxDescription[] => {
    return [...Array(count)].map(
        (): PaxDescription => ({
            IsAdult: age > 17,
            Age: age,
        })
    );
};

const generateHotelRoom = (count: number, productId: string, amount: number): Room[] => {
    const { adults, children, infants } = generateTravellers();

    return [...Array(count)].map(
        (_, index): Room => ({
            Id: `${productId}-${index}`,
            Description:
                '2-rumslägenhet. Allrum med kök och 2 sovpl, sovrum med 2 sovpl. Ca 44 m². 2 av sovpl är i bäddsoffa. Diskmaskin. Balkong eller terrass. \n \n- Grill finns i alla lägenheter. \n- Gratis tillgång till tvättstuga med tvättmaskin och torktumlare \n- Gratis lån av cyklar',
            Pax: {
                Title: 0,
                Gender: 0,
                FirstName: '',
                LastName: '',
                SpecialNeedsOption: null,
                DateOfBirth: null,
                MandatoryFields: [],
                SpecialRequest: null,
                Nationality: null,
            },
            PaxDescription: [
                ...generateHotelPaxDescription(adults, 99),
                ...generateHotelPaxDescription(children, 10),
                ...generateHotelPaxDescription(infants, 1),
            ],
            Price: { Amount: amount, Currency },
            PriceAtLocation: { Amount: 15, Currency: 'EUR' },
            PriceExcludingTaxAndFees: { Amount: amount * 0.9, Currency },
            TaxesAndFees: [
                {
                    Amount: {
                        Amount: amount * 0.1,
                        Currency,
                    },
                    IsIncludedInPrice: true,
                    IsTax: true,
                    Type: 'Tax',
                },
            ],
            CustomerCancellationPolicies: generateCancellationPolicies(
                index,
                amount,
                count,
                `${productId}-${index}`
            ),
            AllNotes: [
                {
                    Scope: 'Hotel',
                    Text: 'This is a hotel note. Avgifter för extragäster kan tillkomma och varierar i enlighet med boendets policy. <br />Statligt utfärdat foto-ID och kreditkort eller kontantdeposition krävs vid incheckning för oförutsedda utgifter. <br />Särskilda önskemål erbjuds i mån av tillgång vid incheckning och kan medföra ytterligare avgifter. Särskilda önskemål kan inte garanteras. <ul>  </ul> Om du planerar att ankomma efter 22:00 ska du kontakta hotellet i förväg med kontaktinformationen i bokningsbekräftelsen.',
                },
                {
                    Scope: 'Hotel',
                    Text: 'First hotel note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr. First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr. First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr. First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr. First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.',
                },
                {
                    Scope: 'Room',
                    Text: 'First room note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr. First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr. First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr. First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr. First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.First note for Roomstays Car park NO.Check-in hour 15:00-.Identification card at arr.',
                },
                {
                    Scope: 'Room',
                    Text: 'This is a room note. Avgifter för extragäster kan tillkomma och varierar i enlighet med boendets policy. <br />Statligt utfärdat foto-ID och kreditkort eller kontantdeposition krävs vid incheckning för oförutsedda utgifter. <br />Särskilda önskemål erbjuds i mån av tillgång vid incheckning och kan medföra ytterligare avgifter. Särskilda önskemål kan inte garanteras. <ul>  </ul> Om du planerar att ankomma efter 22:00 ska du kontakta hotellet i förväg med kontaktinformationen i bokningsbekräftelsen.',
                },
            ],
        })
    );
};

const generateHotel = (index: number, dates: Dates): HotelProduct => {
    const numberOfRooms = index + 1;
    const amount = 1000;
    const roomAmount = amount / numberOfRooms;
    const productId = generateUUID();
    const isRefundable = sample([1, 2]) === 1;

    const discount: AppliedDiscount | null =
        index % 2 === 0
            ? {
                  Name: 'Spring2025',
                  Amount: {
                      Amount: 250,
                      Currency,
                  },
                  IsApplied: true,
              }
            : null;

    const hotel = sample(AvailableHotels) as AvailableHotel;

    return {
        Id: productId,
        // CombinationId: getCombinationId(),
        $type: SerializationType.HotelProduct,
        Type: ProductType.Hotel,
        Destination: hotel.Destination,
        Description: hotel.Description,
        Position: hotel.Position,
        Address: hotel.Address,
        HotelCode: `fake-hotel-code-${index}`,
        IsRefundable: isRefundable,
        PolygonId: hotel.PolygonId,
        Price: { Amount: amount, Currency },
        AppliedDiscount: discount,
        FromDate: getFormattedDate(dates.from),
        ToDate: getFormattedDate(getDateAddingDuration(dates.from, { days: 4 })),
        Tags: [],
        ImageUrl: hotel.ImageUrl,
        Rooms: generateHotelRoom(numberOfRooms, productId, roomAmount),
        Rating: Math.round(random(2, 10)) / 2,
    };
};

const generateAgency = (): Agency | null => {
    if (stored.checkout.hasAgency) {
        return {
            AgencyNumber: '3000',
            Email: 'agency@sembo.com',
            Reference: 'AgencyCompany',
            Phone: '701010100',
            Commission: { Amount: 1000, Currency },
        };
    }

    return null;
};

export const generateAddons = (products: AnyProduct[]): AddonProduct[] => {
    const addons: AddonProduct[] = [];
    const servicePackageAddons: ServicePackageAddon[] = [];
    const hasFlight = products.some((p) => p.Type === ProductType.Flight);

    const generateBasicPrice = () => {
        const affiliate = getAffiliate();
        return affiliate?.AffiliateId ? { Amount: 249, Currency } : { Amount: 0, Currency };
    };

    if (hasFlight) {
        addons.push({
            Id: generateUUID(),
            Type: ProductType.Addon,
            $type: SerializationType.FlightConfirmationSms,
            Tags: [],
            Description: 'Bokningsbekräftelse via SMS (för flyg)',
            SpecialRequest: null,
            Price: { Amount: 25, Currency },
            AppliedDiscount: null,
            ShowDiscardPopUp: false,
            LinkUrl: null,
            IsInsurance: false,
        });
    }

    if (stored.checkout.hasAddonServicePackage) {
        servicePackageAddons.push({
            Id: generateUUID(),
            Type: ProductType.Addon,
            $type: SerializationType.ServicePackage,
            Tags: ['ShowOnCheckoutPage', 'ServicePackage'],
            Description:
                'Support when your travel is disrupted — Get your 249 kr back as a voucher.',
            SpecialRequest: null,
            Ordinal: 1,
            Price: generateBasicPrice(),
            OriginalPrice: { Amount: 249, Currency },
            AppliedDiscount: null,
            ShowDiscardPopUp: true,
            LinkUrl: 'https://www.sembo.se/pages/terms?#servicepaket',
            IsInsurance: false,
        });
        servicePackageAddons.push({
            Id: generateUUID(),
            Type: ProductType.Addon,
            $type: SerializationType.ServicePackage,
            Tags: ['ShowOnCheckoutPage', 'ServicePackage'],
            Description: 'In addition to the Basic Service you also get:',
            SpecialRequest: null,
            Ordinal: 2,
            Price: { Amount: 499, Currency },
            OriginalPrice: { Amount: 499, Currency },
            AppliedDiscount: null,
            ShowDiscardPopUp: true,
            LinkUrl: 'https://www.sembo.se/pages/terms?#servicepaket',
            IsInsurance: false,
        });
    }

    if (stored.checkout.hasAddonLuggage && hasFlight) {
        addons.push({
            Id: generateUUID(),
            Type: ProductType.Addon,
            $type: SerializationType.LuggageInsurance,
            Tags: ['ShowOnCheckoutPage', 'LuggageInsurance'],
            Description: 'Luggage Insurance',
            SpecialRequest: null,
            Price: { Amount: 600, Currency },
            AppliedDiscount: null,
            ShowDiscardPopUp: true,
            LinkUrl: 'https://www.sembo.se/pages/baggage-insurance',
            IsInsurance: true,
        });
    }

    if (stored.checkout.hasAddonCancellation) {
        addons.push({
            Id: generateUUID(),
            Type: ProductType.Addon,
            $type: SerializationType.CancellationInsurance,
            Tags: ['ShowOnCheckoutPage', 'CancellationInsurance'],
            Description: 'Avbeställningsskydd',
            Price: { Amount: 420, Currency },
            AppliedDiscount: null,
            ShowDiscardPopUp: true,
            LinkUrl: 'https://www.sembo.se/pages/terms?#cancellation_insurance',
            IsInsurance: true,
        });
    }

    if (stored.checkout.hasAddonTravelInsurance) {
        addons.push({
            Id: generateUUID(),
            Type: ProductType.Addon,
            $type: SerializationType.TravelInsurance,
            Tags: ['ShowOnCheckoutPage', 'TravelInsurance'],
            Description: 'Travel Insurance provided by Solid',
            Price: { Amount: 280, Currency },
            AppliedDiscount: null,
            ShowDiscardPopUp: true,
            LinkUrl: 'https://www.sembo.se/pages/terms?#travel_insurance',
            IsInsurance: true,
        });
    }

    return [...servicePackageAddons, ...addons];
};

const generateBonusNights = (count: number): Bonus[] => {
    const bonus: Bonus[] = [];

    if (stored.checkout.hasBonusNights) {
        return [...Array(count)].map((_, index): Bonus => {
            const amount = 420 * index;

            return {
                Id: generateUUID(),
                Type: 'BonusNight',
                Value: { Amount: amount, Currency },
                IsApplied: false,
            };
        });
    }

    return bonus;
};

const generatePaymentDiscount = (): Money | null => {
    if (stored.checkout.hasPaymentDiscount) {
        return {
            Amount: 150,
            Currency,
        };
    }

    return null;
};

const generateCampaignDiscount = (): Money | null => {
    if (stored.checkout.hasCampaignDiscount) {
        return {
            Amount: 63,
            Currency,
        };
    }

    return null;
};

const generateAmountAlreadyPaid = (): Money => {
    return {
        Amount: stored.checkout.hasAmountAlreadyPaid ? 100 : 0,
        Currency,
    };
};

const getAffiliate = (): CartAffiliate | null => {
    const { enabled, affiliateId, businessArea, salesChannel, groupName } =
        stored.checkout.affiliate;

    if (enabled) {
        return {
            AffiliateId: affiliateId,
            SalesChannel: salesChannel,
            BusinessArea: businessArea,
            GroupName: groupName,
        };
    }

    return null;
};

const getBookingSalesChannels = (): BookingSalesChannel[] => {
    return stored.checkout.bookingSalesChannels ?? [];
};

const generateProducts = (): AnyProduct[] => {
    const trips: Trip[] = [];

    const fakeTrips = groupBy(stored.products, 'trip.id');

    Object.keys(fakeTrips).map((x, index) => {
        const previousTrip = trips[index - 1];

        const trip = generateTrip(fakeTrips[x], previousTrip);
        trips.push(trip);
    });

    return trips.flatMap((x) => x.products);
};

const findClosestPreviousProduct = (
    tripProducts: AnyProduct[],
    index?: number
): AnyProduct | null => {
    const supported = [ProductType.Flight, ProductType.Ferry, ProductType.Hotel];

    const validProducts = tripProducts
        .slice(0, index)
        .filter((product) => supported.includes(product.Type));

    return validProducts.length > 0 ? validProducts[validProducts.length - 1] : null;
};

function hasRoundTrip(p: FakeDataProduct): p is FakeDataProductFlight | FakeDataProductFerry {
    return p.type === 'Flight' || p.type === 'Ferry';
}

const generateTrip = (products: FakeDataProduct[], previousTrip: Trip): Trip => {
    const initialFromDate = previousTrip?.dates?.to ?? new Date();

    const fakeRoundTrip = products.find(
        (x): x is FakeDataProductFlight | FakeDataProductFerry =>
            hasRoundTrip(x) && x.isRoundTrip
    );

    const fakeProducts = products.filter((x) => !hasRoundTrip(x) || !x.isRoundTrip);

    const tripProducts: AnyProduct[] = [];

    fakeProducts.forEach((p, index) => {
        const previousProduct: AnyProduct | null = findClosestPreviousProduct(
            tripProducts,
            index
        );

        const previousFromDate = previousProduct
            ? getProductFromDate(previousProduct, false)
            : initialFromDate;

        const previousToDate = previousProduct
            ? getProductToDate(previousProduct, false)
            : null;

        switch (p.type) {
            case 'Flight': {
                const dates: Dates = previousToDate
                    ? {
                          from: getDateAddingDuration(previousToDate, {
                              hours: 4,
                          }),
                      }
                    : { from: initialFromDate };

                tripProducts.push(
                    generateFlight(index, dates.from, null, { carrierCode: p.platingCarrier })
                );
                break;
            }
            case 'Ferry': {
                const dates: Dates = previousToDate
                    ? {
                          from: getDateAddingDuration(previousToDate, {
                              hours: 4,
                          }),
                      }
                    : { from: initialFromDate };

                tripProducts.push(generateFerry(index, dates.from, null));
                break;
            }
            case 'Hotel': {
                const dates: Dates = previousToDate
                    ? {
                          from: getDateAddingDuration(previousToDate, {
                              hours: 6,
                          }),
                      }
                    : { from: initialFromDate };

                tripProducts.push(generateHotel(index, dates));
                break;
            }
            case 'Activity': {
                const dates: Dates = previousFromDate
                    ? {
                          from: getDateAddingDuration(previousFromDate, {
                              hours: 10,
                          }),
                      }
                    : { from: initialFromDate };

                tripProducts.push(generateActivity(index, dates));
                break;
            }
        }
    });

    if (fakeRoundTrip) {
        const first: AnyProduct | null = tripProducts[0];
        const fromDate: Date | null = first ? getProductFromDate(first) : null;

        const last: AnyProduct | null = findClosestPreviousProduct(tripProducts);

        const toDate: Date | null = last ? getProductToDate(last) : null;

        const outboundDate = fromDate
            ? fromDate
            : getDateSubtractingDuration(new Date(), {
                  days: 1,
              });

        const inboundDate = toDate
            ? toDate
            : getDateAddingDuration(outboundDate, {
                  days: 4,
              });

        switch (fakeRoundTrip.type) {
            case 'Flight': {
                tripProducts.unshift(
                    generateFlight(0, outboundDate, inboundDate, {
                        isRoundTrip: fakeRoundTrip.isRoundTrip,
                        carrierCode: fakeRoundTrip.platingCarrier,
                    })
                );
                break;
            }
            case 'Ferry': {
                tripProducts.unshift(
                    generateFerry(0, outboundDate, inboundDate, {
                        isRoundTrip: fakeRoundTrip.isRoundTrip,
                    })
                );
                break;
            }
        }
    }

    const tripFirstProduct = tripProducts[0];
    const tripLastProduct = tripProducts[tripProducts.length - 1];

    const tripFrom =
        (tripFirstProduct && getProductFromDate(tripFirstProduct)) ?? initialFromDate;

    const tripTo = fakeRoundTrip
        ? getProductToDate(tripFirstProduct)
        : getProductToDate(tripLastProduct);

    return {
        products: tripProducts,
        dates: { from: tripFrom, to: tripTo },
        isRoundTrip: Boolean(fakeRoundTrip),
    };
};

const getDataObject = (): Cart => {
    const products = generateProducts();

    return {
        ShoppingCartKey: generateUUID(),
        FlowContainsPreCheckout: true,
        BookingToken: getFakeBookingToken(),
        Customer: {
            Email: 'customer@sembo.com',
            PhoneNumber: '701010100',
            ReceiveNewsletter: true,
            CountryCode: '',
            IsLoggedIn: stored.auth.enabled ?? false,
        },
        Agency: generateAgency(),
        EstimatedMargin: {
            Amount: 199,
            Currency,
        },
        ProductCombination: null,
        Products: products,
        AvailableAddons: generateAddons(products),
        AvailableBonus: generateBonusNights(3),
        NextUrl: '',
        ErrorMessage: null,
        ExpirationInSeconds: 0,
        RedirectUrlOnExpiration: 'https://www.sembo.se/',
        FlowContainsPostCheckout:
            stored.products.filter((x) => x.type === 'Flight').length === 1,
        Affiliate: getAffiliate(),
        BookingSalesChannels: getBookingSalesChannels(),
        Payment: {
            PaymentId: 'the-payment-id',
            PaymentDiscount: generatePaymentDiscount(),
            CampaignDiscount: generateCampaignDiscount(),
            AmountAlreadyPaid: generateAmountAlreadyPaid(),
            DiscountedPaymentMethods: ['TRUSTLY', 'MC'],
            PaymentMethods: [
                {
                    $type: 'AdyenPaymentMethod',
                    Key: undefined,
                    EncryptedData: undefined,
                    BrowserInfo: undefined,
                    ShopperIP: undefined,
                    PaymentResponse: undefined,
                    Issuer: undefined,
                    Bic: undefined,
                    CardLastDigits: undefined,
                    CardBrand: undefined,
                    BinValue: undefined,
                    Type: 0,
                    Name: 'Adyen',
                    Code: 'Adyen',
                    Logo: '',
                    HasDiscount: false,
                    Discount: undefined,
                },
                {
                    $type: 'LeaBankPaymentMethod',
                    Name: 'LeaBank',
                    Code: 'LeaBank',
                    Logo: 'https://images.sembo.se/common/pay/captum.png',
                    HasDiscount: false,
                    Discount: undefined,
                    Type: 6,
                },
                {
                    Type: 4,
                    Name: 'GiftCard',
                    Code: 'GC',
                    Logo: undefined,
                    HasDiscount: false,
                    Discount: undefined,
                },
            ],
            SelectedPaymentOption: null,
            SelectedPartPaymentOption: 0,
            GiftCards: [],
            PaymentDueTime: getFormattedDate(
                getDateAddingDuration(new Date(), { days: 10, hours: 2 }),
                DateFormat.ISO
            ),
            PartPaymentOptions: [
                {
                    Amount: { Amount: 2000, Currency },
                    DueDateForNextPayment: getFormattedDate(
                        getDateAddingDuration(new Date(), {
                            days: 10,
                            hours: 2,
                        }),
                        DateFormat.ISO
                    ),
                    Type: 1,
                    AvailablePaymentOptionTypes: [
                        PaymentMethodType.Adyen,
                        PaymentMethodType.LeaBank,
                        PaymentMethodType.GiftCard,
                    ],
                },
                {
                    Amount: { Amount: 1000, Currency },
                    DueDateForNextPayment: getFormattedDate(
                        getDateAddingDuration(new Date(), {
                            days: 11,
                            hours: 2,
                        }),
                        DateFormat.ISO
                    ),
                    Type: 2,
                    AvailablePaymentOptionTypes: [PaymentMethodType.Adyen],
                },
            ],
        },
    };
};

const getFakeData = () => {
    try {
        return getDataObject();
    } catch (error) {
        console.error('Error trying to create fake data', error);
        setStoredFakeData(null);
        stored = getStoredFakeData();
        return getDataObject();
    }
};

export const fakeCartData: Cart = getFakeData();
